import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { CloseOutlined } from '@ant-design/icons';
import { Flex } from 'antd';

import { useAppDispatch } from '@/shared/lib/store';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { TitleWithHelp } from '@/shared/ui/title-with-help';

import { removeStudiedPattern } from '../../model/slices/pattern-lab.slice';
import type { PatternStudy } from '../../model/types/pattern-study';

import cls from './pattern-lab-selector.module.css';

type PatternStudyItemProps = {
    patternStudy: PatternStudy;
};

type PatternComparatorProps = {
    patterns: PatternStudy[];
};

const PatternStudyItem = memo(({ patternStudy }: PatternStudyItemProps) => {
    const { t } = useTranslation('pattern-studio');

    const dispatch = useAppDispatch();

    const handleRemovePattern = () => {
        dispatch(removeStudiedPattern(patternStudy.pattern.unique_id));
    };

    return (
        <div className={cls.item}>
            <div className={cls.cardWrapper}>
                <Card accentColor={patternStudy.color}>
                    <Flex
                        gap={16}
                        wrap
                    >
                        <div style={{ flex: 1 }}>
                            <TitleWithHelp
                                helpId="PATTERN_ID"
                                title={t('patternStats.patternId')}
                            />
                            <div className={cls.itemProperty}>
                                {patternStudy.pattern.unique_id}
                            </div>
                        </div>
                        <div style={{ flex: 1 }}>
                            <TitleWithHelp
                                helpId="PATTERN_NICKNAME"
                                title={t('patternStats.nickname')}
                            />
                            <div className={cls.itemProperty}>
                                {patternStudy.name}
                            </div>
                        </div>
                    </Flex>
                </Card>
                <Button
                    className={cls.removeButton}
                    icon={<CloseOutlined />}
                    onClick={handleRemovePattern}
                    variant="ghost"
                />
            </div>
        </div>
    );
});

PatternStudyItem.displayName = 'PatternStudyItem';

export const PatternLabSelector = memo(
    ({ patterns }: PatternComparatorProps) => (
        <Flex
            data-tour="pattern-lab-selector"
            gap="16px"
            style={{ width: '100%' }}
        >
            {patterns.map((patternStudy) => (
                <PatternStudyItem
                    key={patternStudy.pattern.unique_id}
                    patternStudy={patternStudy}
                />
            ))}
        </Flex>
    ),
);

PatternLabSelector.displayName = 'PatternComparator';
